/**
 * Machines à états métier (§13 du cahier des charges) : la référence partagée des
 * états et des transitions permises. Elle ne protège rien par elle-même. Le
 * serveur refuse une transition interdite avec l'erreur métier stable
 * `transition_interdite`, sans effet partiel : soit en appelant `canTransition`
 * (ex. `userFsm` dans la modification d'un membre et dans la console de la
 * plateforme), soit par une garde écrite dans le domaine (ex. inventaire).
 * `privileged` : transitions réservées à un rôle responsable (patron/dispatcher),
 * toujours auditées avec motif.
 */

export interface StateMachine<S extends string> {
  readonly name: string;
  readonly initial: S;
  readonly states: readonly S[];
  readonly transitions: Readonly<Partial<Record<S, readonly S[]>>>;
  readonly privileged?: Readonly<Partial<Record<S, readonly S[]>>>;
  /** États terminaux : plus aucune transition standard. */
  readonly terminal: readonly S[];
}

export function canTransition<S extends string>(
  m: StateMachine<S>,
  from: S,
  to: S,
  opts: { privileged?: boolean } = {},
): boolean {
  if ((m.transitions[from] ?? []).includes(to)) return true;
  if (opts.privileged && (m.privileged?.[from] ?? []).includes(to)) return true;
  return false;
}

// --- Demande client (QR) -----------------------------------------------------
export type ServiceRequestState =
  | 'soumise'
  | 'a_qualifier'
  | 'information_requise'
  | 'acceptee'
  | 'rejetee'
  | 'convertie';

export const serviceRequestFsm: StateMachine<ServiceRequestState> = {
  name: 'service_request',
  initial: 'soumise',
  states: ['soumise', 'a_qualifier', 'information_requise', 'acceptee', 'rejetee', 'convertie'],
  transitions: {
    soumise: ['a_qualifier'],
    a_qualifier: ['information_requise', 'acceptee', 'rejetee'],
    information_requise: ['a_qualifier', 'rejetee'],
    acceptee: ['convertie'], // conversion idempotente, demande source conservée
  },
  terminal: ['rejetee', 'convertie'],
};

// --- Intervention --------------------------------------------------------------
export type InterventionState =
  | 'planifiee'
  | 'en_route'
  | 'en_cours'
  | 'en_pause'
  | 'cloturee'
  | 'annulee';

export const interventionFsm: StateMachine<InterventionState> = {
  name: 'intervention',
  initial: 'planifiee',
  states: ['planifiee', 'en_route', 'en_cours', 'en_pause', 'cloturee', 'annulee'],
  transitions: {
    planifiee: ['en_route', 'en_cours', 'annulee'],
    en_route: ['en_cours', 'planifiee', 'annulee'],
    en_cours: ['en_pause', 'cloturee'],
    en_pause: ['en_cours', 'cloturee'],
  },
  // Réouverture responsable (Q173) : crée une révision, l'original reste immuable.
  privileged: {
    cloturee: ['en_cours'],
    en_cours: ['annulee'],
    en_pause: ['annulee'],
  },
  terminal: ['annulee'],
};

/** Étape terrain enregistrée par une transition (états §13). */
const TERRAIN_EVENT_BY_TARGET: Partial<Record<InterventionState, string>> = {
  en_route: 'depart',
  en_cours: 'debut',
  en_pause: 'pause',
  cloturee: 'cloture',
  annulee: 'annulation',
  planifiee: 'replanification',
};

/**
 * Étape de la visite qu'une transition enregistre. Revenir d'une pause est une
 * REPRISE, pas un second début ; arriver au bout du trajet est une ARRIVÉE, qui
 * clôt le déplacement et commence le travail (15/10/2026).
 */
export function terrainEventKind(from: InterventionState, to: InterventionState): string {
  if (to === 'en_cours' && from === 'en_pause') return 'reprise';
  if (to === 'en_cours' && from === 'en_route') return 'arrivee';
  return TERRAIN_EVENT_BY_TARGET[to] ?? to;
}

/**
 * Q59 : la position n'est relevée qu'aux étapes que l'entreprise a choisies
 * (`geolocation_events` : départ, arrivée, clôture). Renvoie le réglage qui
 * couvre cette étape, ou `null` : aucune position ne doit être collectée.
 */
export function geolocationSettingFor(eventKind: string): 'depart' | 'arrivee' | 'cloture' | null {
  if (eventKind === 'depart') return 'depart';
  if (eventKind === 'debut' || eventKind === 'arrivee') return 'arrivee';
  if (eventKind === 'cloture') return 'cloture';
  return null;
}

// --- Devis -----------------------------------------------------------------------
export type QuoteState =
  | 'brouillon'
  | 'envoye'
  | 'consulte'
  | 'accepte'
  | 'refuse'
  | 'expire'
  | 'annule'
  | 'revise';

export const quoteFsm: StateMachine<QuoteState> = {
  name: 'quote',
  initial: 'brouillon',
  states: ['brouillon', 'envoye', 'consulte', 'accepte', 'refuse', 'expire', 'annule', 'revise'],
  transitions: {
    brouillon: ['envoye', 'annule'],
    envoye: ['consulte', 'accepte', 'refuse', 'expire', 'revise', 'annule'],
    consulte: ['accepte', 'refuse', 'expire', 'revise', 'annule'],
    expire: ['revise'],
    refuse: ['revise'],
  },
  terminal: ['accepte', 'annule', 'revise'],
};

// --- Facture -----------------------------------------------------------------------
export type InvoiceState =
  | 'brouillon'
  | 'emise'
  | 'partiellement_payee'
  | 'payee'
  | 'en_retard'
  | 'litigieuse'
  | 'creditee';

export const invoiceFsm: StateMachine<InvoiceState> = {
  name: 'invoice',
  initial: 'brouillon',
  states: [
    'brouillon',
    'emise',
    'partiellement_payee',
    'payee',
    'en_retard',
    'litigieuse',
    'creditee',
  ],
  transitions: {
    brouillon: ['emise'], // annulation directe réservée au brouillon (suppression logique)
    emise: ['partiellement_payee', 'payee', 'en_retard', 'litigieuse', 'creditee'],
    // « émise » : son seul paiement annulé (lot BIL-5).
    partiellement_payee: ['payee', 'en_retard', 'litigieuse', 'creditee', 'emise'],
    en_retard: ['partiellement_payee', 'payee', 'litigieuse', 'creditee'],
    // Litige levé : l'état repart des paiements, des avoirs et de l'échéance, « émise » comprise.
    litigieuse: ['emise', 'partiellement_payee', 'payee', 'creditee', 'en_retard'],
    // Paiement saisi par erreur puis annulé par contre-passation (lot BIL-5) : l'état repart
    // des paiements restants et de l'échéance. Un avoir ne la fait pas changer d'état.
    payee: ['emise', 'partiellement_payee', 'en_retard'],
  },
  terminal: ['creditee'],
};

// --- Contrat --------------------------------------------------------------------------
export type ContractState =
  | 'brouillon'
  | 'actif'
  | 'en_pause'
  | 'a_renouveler'
  | 'termine'
  | 'resilie';

export const contractFsm: StateMachine<ContractState> = {
  name: 'contract',
  initial: 'brouillon',
  states: ['brouillon', 'actif', 'en_pause', 'a_renouveler', 'termine', 'resilie'],
  transitions: {
    brouillon: ['actif'],
    actif: ['en_pause', 'a_renouveler', 'resilie'],
    en_pause: ['actif', 'resilie'],
    a_renouveler: ['actif', 'termine', 'resilie'], // renouvellement MANUEL (Q63)
  },
  terminal: ['termine', 'resilie'],
};

// --- Commande fournisseur -------------------------------------------------------------------
export type PurchaseOrderState =
  | 'demande'
  | 'a_approuver'
  | 'approuvee'
  | 'envoyee'
  | 'partiellement_recue'
  | 'recue'
  | 'cloturee'
  | 'annulee'
  | 'retournee';

export const purchaseOrderFsm: StateMachine<PurchaseOrderState> = {
  name: 'purchase_order',
  initial: 'demande',
  states: [
    'demande',
    'a_approuver',
    'approuvee',
    'envoyee',
    'partiellement_recue',
    'recue',
    'cloturee',
    'annulee',
    'retournee',
  ],
  transitions: {
    demande: ['a_approuver', 'approuvee', 'annulee'], // approbation auto selon seuils (Q194)
    a_approuver: ['approuvee', 'annulee'],
    approuvee: ['envoyee', 'annulee'],
    envoyee: ['partiellement_recue', 'recue', 'annulee'],
    partiellement_recue: ['partiellement_recue', 'recue', 'cloturee', 'retournee'],
    recue: ['cloturee', 'retournee'],
    retournee: ['cloturee', 'partiellement_recue', 'recue'],
  },
  terminal: ['cloturee', 'annulee'],
};

// --- Inventaire ---------------------------------------------------------------------------------
export type InventoryState = 'brouillon' | 'en_cours' | 'a_valider' | 'valide' | 'annule';

export const inventoryFsm: StateMachine<InventoryState> = {
  name: 'inventory',
  initial: 'brouillon',
  states: ['brouillon', 'en_cours', 'a_valider', 'valide', 'annule'],
  transitions: {
    brouillon: ['en_cours', 'annule'],
    en_cours: ['a_valider', 'annule'],
    a_valider: ['valide', 'en_cours', 'annule'],
  },
  terminal: ['valide', 'annule'],
};

// --- Intégration externe (job sortant) ------------------------------------------------------------
export type IntegrationJobState =
  | 'en_file'
  | 'en_cours'
  | 'a_reessayer'
  | 'reussie'
  | 'echec_final'
  | 'annulee';

export const integrationJobFsm: StateMachine<IntegrationJobState> = {
  name: 'integration_job',
  initial: 'en_file',
  states: ['en_file', 'en_cours', 'a_reessayer', 'reussie', 'echec_final', 'annulee'],
  transitions: {
    en_file: ['en_cours', 'annulee'],
    en_cours: ['reussie', 'a_reessayer', 'echec_final'],
    a_reessayer: ['en_cours', 'annulee', 'echec_final'],
    echec_final: ['en_file'], // replay manuel (nouveau job lié, historique conservé)
  },
  terminal: ['reussie', 'annulee'],
};

// --- Contrôle d'étanchéité ---------------------------------------------------------------------------
export type LeakCheckState =
  | 'conforme'
  | 'fuite'
  | 'non_applicable'
  | 'reparation_requise'
  | 'recontrole_requis'
  | 'interdit_remise_service';

export const leakCheckFsm: StateMachine<LeakCheckState> = {
  name: 'leak_check',
  initial: 'non_applicable',
  states: [
    'conforme',
    'fuite',
    'non_applicable',
    'reparation_requise',
    'recontrole_requis',
    'interdit_remise_service',
  ],
  transitions: {
    non_applicable: ['conforme', 'fuite'],
    // Un équipement « conforme » peut fuir lors d'un contrôle périodique
    // ULTÉRIEUR : cet état n'est pas terminal (défaut détecté par l'audit).
    conforme: ['conforme', 'fuite', 'non_applicable'],
    fuite: ['reparation_requise', 'interdit_remise_service'],
    reparation_requise: ['recontrole_requis', 'interdit_remise_service'],
    recontrole_requis: ['conforme', 'fuite'],
    interdit_remise_service: ['recontrole_requis'],
  },
  terminal: [], // le cycle de contrôle est périodique, aucun état n'est définitif
};

// --- Machine (cycle de vie, CMP-5) ----------------------------------------------------------------------
export type EquipmentState = 'en_service' | 'a_l_arret' | 'interdit_remise_service' | 'demantele';

/**
 * L'interdiction de remise en service ne se pose et ne se lève que par un contrôle
 * d'étanchéité (`privileged` : réservé à ce contrôle, jamais à la route de statut).
 * Elle se pose aussi sur une machine à l'arrêt : sinon sa remise en service par la
 * route de statut contournerait l'interdiction. Le démantèlement est définitif ;
 * l'archivage (`deleted_at`) n'est pas un état.
 */
export const equipmentFsm: StateMachine<EquipmentState> = {
  name: 'equipment',
  initial: 'en_service',
  states: ['en_service', 'a_l_arret', 'interdit_remise_service', 'demantele'],
  transitions: {
    en_service: ['a_l_arret', 'demantele'],
    a_l_arret: ['en_service', 'demantele'],
    interdit_remise_service: ['demantele'],
  },
  privileged: {
    en_service: ['interdit_remise_service'],
    a_l_arret: ['interdit_remise_service'],
    interdit_remise_service: ['en_service'],
  },
  terminal: ['demantele'],
};

/**
 * Résultats possibles après le dernier contrôle (aucun : tous). Le même
 * résultat peut se répéter. Une seule règle pour le serveur et pour la saisie
 * faite sans réseau sur l'appareil (FLD-8).
 */
export function nextLeakCheckResults(previous: LeakCheckState | null): LeakCheckState[] {
  if (!previous) return [...leakCheckFsm.states];
  return leakCheckFsm.states.filter((result) => result === previous || canTransition(leakCheckFsm, previous, result));
}

// --- Utilisateur ----------------------------------------------------------------------------------------
export type UserState = 'invite' | 'actif' | 'verrouille' | 'revoque' | 'inactif';

/**
 * Décision 27 (19/10/2026) : un membre retiré (`revoque`) l'est définitivement ;
 * s'il revient, on l'invite de nouveau. `invite → actif` n'a lieu que par le
 * lien d'activation, jamais par une modification du compte.
 */
export const userFsm: StateMachine<UserState> = {
  name: 'user',
  initial: 'invite',
  states: ['invite', 'actif', 'verrouille', 'revoque', 'inactif'],
  transitions: {
    invite: ['actif', 'revoque'],
    actif: ['verrouille', 'inactif', 'revoque'],
    verrouille: ['actif', 'revoque'],
    inactif: ['actif', 'revoque'],
  },
  terminal: ['revoque'],
};

// --- Abonnement SaaS --------------------------------------------------------------------------------------
export type SubscriptionState =
  | 'essai'
  | 'actif'
  | 'grace'
  | 'lecture_seule'
  | 'suspendu'
  | 'termine';

export const subscriptionFsm: StateMachine<SubscriptionState> = {
  name: 'subscription',
  initial: 'essai',
  states: ['essai', 'actif', 'grace', 'lecture_seule', 'suspendu', 'termine'],
  transitions: {
    // Un essai non converti bascule en lecture seule, jamais en résiliation :
    // les données du prospect restent lisibles et `lecture_seule → actif`
    // permet de souscrire plus tard (Q199 : aucune suppression automatique).
    essai: ['actif', 'lecture_seule', 'termine'],
    actif: ['grace', 'termine'],
    grace: ['actif', 'lecture_seule'],
    lecture_seule: ['actif', 'suspendu'],
    suspendu: ['actif', 'termine'], // réversible, aucune suppression automatique (Q199)
  },
  terminal: ['termine'],
};

// --- Demande RGPD ---------------------------------------------------------------------------------------------
export type GdprRequestState =
  | 'recue'
  | 'identite_a_verifier'
  | 'en_traitement'
  | 'executee'
  | 'refusee_motivee'
  | 'cloturee';

export const gdprRequestFsm: StateMachine<GdprRequestState> = {
  name: 'gdpr_request',
  initial: 'recue',
  states: [
    'recue',
    'identite_a_verifier',
    'en_traitement',
    'executee',
    'refusee_motivee',
    'cloturee',
  ],
  transitions: {
    recue: ['identite_a_verifier', 'en_traitement', 'refusee_motivee'],
    identite_a_verifier: ['en_traitement', 'refusee_motivee'],
    en_traitement: ['executee', 'refusee_motivee'],
    executee: ['cloturee'],
    refusee_motivee: ['cloturee'],
  },
  terminal: ['cloturee'],
};

export const ALL_STATE_MACHINES = [
  serviceRequestFsm,
  interventionFsm,
  quoteFsm,
  invoiceFsm,
  contractFsm,
  purchaseOrderFsm,
  inventoryFsm,
  integrationJobFsm,
  leakCheckFsm,
  equipmentFsm,
  userFsm,
  subscriptionFsm,
  gdprRequestFsm,
] as const;

// --- Ordre d'affichage des listes ------------------------------------------------------
// Les onglets d'état des listes (API et web) se lisent ici : les mêmes états que
// la machine, dans l'ordre des écrans (un test vérifie qu'aucun ne manque ni ne s'ajoute).

/** Onglets des devis : les devis tranchés (révisé, annulé) en dernier. */
export const QUOTE_TAB_ORDER = [
  'brouillon', 'envoye', 'consulte', 'accepte', 'refuse', 'expire', 'revise', 'annule',
] as const satisfies readonly QuoteState[];

/** Onglets des factures : ce qui reste à encaisser avant ce qui est réglé. */
export const INVOICE_TAB_ORDER = [
  'brouillon', 'emise', 'partiellement_payee', 'en_retard', 'litigieuse', 'payee', 'creditee',
] as const satisfies readonly InvoiceState[];

/** Onglets des contrats. */
export const CONTRACT_TAB_ORDER = [
  'brouillon', 'actif', 'en_pause', 'a_renouveler', 'termine', 'resilie',
] as const satisfies readonly ContractState[];

/** États non terminaux d'une demande : elle attend encore le bureau. */
export const OPEN_REQUEST_STATES: readonly ServiceRequestState[] = serviceRequestFsm.states.filter(
  (state) => !serviceRequestFsm.terminal.includes(state),
);
